import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  AlertTriangle,
  Plus,
  Search,
  Calendar,
  Clock,
  User,
  MapPin,
  Phone,
  Shield,
  FileText,
  Edit,
  Trash2,
  Filter,
  RefreshCw,
  Users,
  Activity,
  Printer
} from 'lucide-react';
import { toast } from 'sonner';
import ResidentDocumentPrint from './ResidentDocumentPrint';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const IncidentReports = () => {
  const { user } = useAuth();
  const [incidents, setIncidents] = useState([]);
  const [residents, setResidents] = useState([]);
  const [allStaff, setAllStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusUpdateDialog, setStatusUpdateDialog] = useState({ open: false, incident: null });
  const [statusUpdateData, setStatusUpdateData] = useState({ status: '', notes: '' });
  const [filterType, setFilterType] = useState('all');
  const [filterSeverity, setFilterSeverity] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterDays, setFilterDays] = useState('30');
  const [showPrintDialog, setShowPrintDialog] = useState(false);
  const [printResident, setPrintResident] = useState(null);

  const [formData, setFormData] = useState({
    incident_type: '',
    other_description: '',
    severity: 'medium',
    title: '',
    description: '',
    resident_ids: [],
    resident_names: [],
    staff_involved: [],
    location: '',
    injury_involved: false,
    medical_attention_needed: false,
    police_contacted: false,
    follow_up_required: false,
    follow_up_notes: '',
    incident_date: new Date().toISOString().slice(0, 16) // YYYY-MM-DDTHH:MM format
  });

  const incidentTypes = [
    { value: 'resident_resident', label: 'Resident vs Resident', color: 'bg-orange-100 text-orange-800' },
    { value: 'resident_staff', label: 'Resident vs Staff', color: 'bg-red-100 text-red-800' },
    { value: 'non_compliance', label: 'Non-Compliance', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'injury_resident', label: 'Injury to Resident', color: 'bg-red-100 text-red-800' },
    { value: 'injury_staff', label: 'Injury to Staff', color: 'bg-red-100 text-red-800' },
    { value: 'fire_alarm', label: 'Fire Alarm', color: 'bg-red-100 text-red-800' },
    { value: 'police_inquiry', label: 'Police Looking for Resident', color: 'bg-purple-100 text-purple-800' },
    { value: 'property_damage', label: 'Property Damage', color: 'bg-orange-100 text-orange-800' },
    { value: 'medical_emergency', label: 'Medical Emergency', color: 'bg-red-100 text-red-800' },
    { value: 'theft_suspicion', label: 'Theft/Suspicion', color: 'bg-red-100 text-red-800' },
    { value: 'drug_alcohol', label: 'Drug/Alcohol Related', color: 'bg-red-100 text-red-800' },
    { value: 'visitor_issue', label: 'Visitor Issue', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'other', label: 'Other (specify below)', color: 'bg-gray-100 text-gray-800' }
  ];

  const severityLevels = [
    { value: 'low', label: 'Low', color: 'bg-green-100 text-green-800' },
    { value: 'medium', label: 'Medium', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'high', label: 'High', color: 'bg-orange-100 text-orange-800' },
    { value: 'critical', label: 'Critical', color: 'bg-red-100 text-red-800' }
  ];

  const statusOptions = [
    { value: 'open', label: 'Open', color: 'bg-blue-100 text-blue-800' },
    { value: 'closed', label: 'Closed', color: 'bg-gray-100 text-gray-800' },
    { value: 'resolved', label: 'Resolved', color: 'bg-green-100 text-green-800' }
  ];

  useEffect(() => {
    fetchIncidents();
    fetchResidents();
    fetchStaff();
  }, [filterType, filterSeverity, filterStatus, searchTerm, filterDays]);

  const fetchIncidents = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterType && filterType !== 'all') params.append('incident_type', filterType);
      if (filterSeverity && filterSeverity !== 'all') params.append('severity', filterSeverity);
      if (filterStatus && filterStatus !== 'all') params.append('status', filterStatus);
      if (searchTerm) params.append('search', searchTerm);
      if (filterDays) params.append('days', filterDays);

      const response = await axios.get(`${API}/incident-reports?${params}`);
      setIncidents(response.data);
    } catch (error) {
      console.error('Error fetching incidents:', error);
      toast.error('Failed to load incident reports');
    } finally {
      setLoading(false);
    }
  };

  const fetchResidents = async () => {
    try {
      const response = await axios.get(`${API}/residents?status=current`);
      setResidents(response.data);
    } catch (error) {
      console.error('Error fetching residents:', error);
    }
  };

  const fetchStaff = async () => {
    try {
      const response = await axios.get(`${API}/staff`);
      setAllStaff(response.data);
    } catch (error) {
      console.error('Error fetching staff:', error);
    }
  };

  const handleStatusUpdate = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/incident-reports/${statusUpdateDialog.incident.id}/status`, {
        incident_id: statusUpdateDialog.incident.id,
        status: statusUpdateData.status,
        notes: statusUpdateData.notes
      });
      
      toast.success('Incident status updated successfully');
      setStatusUpdateDialog({ open: false, incident: null });
      setStatusUpdateData({ status: '', notes: '' });
      fetchIncidents();
    } catch (error) {
      console.error('Failed to update incident status:', error);
      toast.error('Failed to update incident status');
    }
  };

  const openStatusDialog = (incident) => {
    setStatusUpdateDialog({ open: true, incident });
    setStatusUpdateData({ status: incident.status, notes: '' });
  };

  const handleDeleteIncident = async (incidentId) => {
    if (!window.confirm('Are you sure you want to delete this incident report? This action cannot be undone.')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/incident-reports/${incidentId}`);
      toast.success('Incident report deleted successfully');
      fetchIncidents();
    } catch (error) {
      console.error('Failed to delete incident report:', error);
      toast.error('Failed to delete incident report');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.incident_type || !formData.title || !formData.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const submitData = {
        ...formData,
        incident_date: new Date(formData.incident_date).toISOString()
      };

      await axios.post(`${API}/incident-reports`, submitData);
      toast.success('Incident report created successfully! It will appear at the top of the list.');
      resetForm();
      setShowAddDialog(false); // Close the dialog
      fetchIncidents(); // Refresh the list
    } catch (error) {
      console.error('Error creating incident report:', error);
      toast.error('Failed to create incident report');
    }
  };

  const resetForm = () => {
    setFormData({
      incident_type: '',
      other_description: '',
      severity: 'medium',
      title: '',
      description: '',
      resident_ids: [],
      resident_names: [],
      staff_involved: [],
      location: '',
      injury_involved: false,
      medical_attention_needed: false,
      police_contacted: false,
      follow_up_required: false,
      follow_up_notes: '',
      incident_date: new Date().toISOString().slice(0, 16)
    });
    setShowAddDialog(false);
  };

  const handleResidentSelect = (residentId, residentName, checked) => {
    if (checked) {
      setFormData({
        ...formData,
        resident_ids: [...formData.resident_ids, residentId],
        resident_names: [...formData.resident_names, residentName]
      });
    } else {
      setFormData({
        ...formData,
        resident_ids: formData.resident_ids.filter(id => id !== residentId),
        resident_names: formData.resident_names.filter(name => name !== residentName)
      });
    }
  };

  const getIncidentTypeDetails = (type) => {
    return incidentTypes.find(t => t.value === type) || { label: type, color: 'bg-gray-100 text-gray-800' };
  };

  const getSeverityDetails = (severity) => {
    return severityLevels.find(s => s.value === severity) || { label: severity, color: 'bg-gray-100 text-gray-800' };
  };

  const getStatusDetails = (status) => {
    return statusOptions.find(s => s.value === status) || { label: status, color: 'bg-gray-100 text-gray-800' };
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      timeZone: 'America/Chicago',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const handlePrintIncidentReports = (resident = null) => {
    if (resident) {
      // Print for specific resident
      const residentIncidents = incidents.filter(incident => 
        incident.resident_ids && incident.resident_ids.includes(resident.id)
      );
      setPrintResident({ ...resident, incidentReports: residentIncidents });
    } else {
      // Print all incidents (would need a different approach)
      toast.info('Please select a specific resident to print incident reports');
      return;
    }
    setShowPrintDialog(true);
  };

  // Filter incidents based on search term
  const filteredIncidents = incidents.filter(incident =>
    incident.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    incident.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    incident.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    incident.resident_names.some(name => name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Incident Reports</h1>
            <p className="text-gray-600">Document and track shelter incidents</p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 gap-6">
          {[...Array(3)].map((_, i) => (
            <Card key={i} className="card-shadow">
              <CardContent className="p-6">
                <div className="skeleton h-32 w-full rounded"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-3">
            <AlertTriangle className="h-8 w-8 text-red-600" />
            <span>Incident Reports</span>
          </h1>
          <p className="text-gray-600 mt-1">Document and track shelter incidents for safety and compliance</p>
        </div>
        
        <div className="mt-4 sm:mt-0 flex items-center space-x-3">
          <Button onClick={fetchIncidents} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button className="bg-red-600 hover:bg-red-700 text-white">
                <Plus className="h-4 w-4 mr-2" />
                Report Incident
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-red-700 flex items-center space-x-2">
                  <AlertTriangle className="h-5 w-5" />
                  <span>Create Incident Report</span>
                </DialogTitle>
                <DialogDescription>
                  Document an incident that occurred at the shelter for safety tracking and compliance
                </DialogDescription>
              </DialogHeader>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Incident Type *</label>
                    <Select value={formData.incident_type} onValueChange={(value) => setFormData({...formData, incident_type: value})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select incident type" />
                      </SelectTrigger>
                      <SelectContent>
                        {incidentTypes.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {formData.incident_type === 'other' && (
                    <div className="md:col-span-2">
                      <label className="text-sm font-medium text-gray-700">Other Incident Type Description *</label>
                      <Input
                        required
                        value={formData.other_description}
                        onChange={(e) => setFormData({...formData, other_description: e.target.value})}
                        placeholder="Please specify the type of incident"
                      />
                    </div>
                  )}

                  <div>
                    <label className="text-sm font-medium text-gray-700">Severity Level *</label>
                    <Select value={formData.severity} onValueChange={(value) => setFormData({...formData, severity: value})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {severityLevels.map((level) => (
                          <SelectItem key={level.value} value={level.value}>
                            {level.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="md:col-span-2">
                    <label className="text-sm font-medium text-gray-700">Incident Title *</label>
                    <Input
                      required
                      value={formData.title}
                      onChange={(e) => setFormData({...formData, title: e.target.value})}
                      placeholder="Brief summary of the incident"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700">Date & Time *</label>
                    <Input
                      type="datetime-local"
                      required
                      value={formData.incident_date}
                      onChange={(e) => setFormData({...formData, incident_date: e.target.value})}
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700">Location</label>
                    <Input
                      value={formData.location}
                      onChange={(e) => setFormData({...formData, location: e.target.value})}
                      placeholder="Where did this occur? (Room, common area, etc.)"
                    />
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label className="text-sm font-medium text-gray-700">Detailed Description *</label>
                  <Textarea
                    required
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Provide a detailed description of what happened, who was involved, and any immediate actions taken..."
                    rows={4}
                  />
                </div>

                {/* Residents Involved */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-3 block">Residents Involved</label>
                  <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-3">
                    {residents.length === 0 ? (
                      <p className="text-gray-500 text-sm">No current residents found</p>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {residents.map((resident) => (
                          <div key={resident.id} className="flex items-center space-x-2">
                            <Checkbox
                              id={`resident-${resident.id}`}
                              checked={formData.resident_ids.includes(resident.id)}
                              onCheckedChange={(checked) => 
                                handleResidentSelect(resident.id, `${resident.first_name} ${resident.last_name}`, checked)
                              }
                            />
                            <label 
                              htmlFor={`resident-${resident.id}`} 
                              className="text-sm text-gray-700 cursor-pointer"
                            >
                              {resident.first_name} {resident.last_name}
                              {resident.room_number && ` - Room ${resident.room_number}`}
                            </label>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Incident Flags */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="injury"
                        checked={formData.injury_involved}
                        onCheckedChange={(checked) => setFormData({...formData, injury_involved: checked})}
                      />
                      <label htmlFor="injury" className="text-sm font-medium text-gray-700">
                        Injury Involved
                      </label>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="medical"
                        checked={formData.medical_attention_needed}
                        onCheckedChange={(checked) => setFormData({...formData, medical_attention_needed: checked})}
                      />
                      <label htmlFor="medical" className="text-sm font-medium text-gray-700">
                        Medical Attention Needed
                      </label>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="police"
                        checked={formData.police_contacted}
                        onCheckedChange={(checked) => setFormData({...formData, police_contacted: checked})}
                      />
                      <label htmlFor="police" className="text-sm font-medium text-gray-700">
                        Police Contacted
                      </label>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="followup"
                        checked={formData.follow_up_required}
                        onCheckedChange={(checked) => setFormData({...formData, follow_up_required: checked})}
                      />
                      <label htmlFor="followup" className="text-sm font-medium text-gray-700">
                        Follow-up Required
                      </label>
                    </div>
                  </div>
                </div>

                {formData.follow_up_required && (
                  <div>
                    <label className="text-sm font-medium text-gray-700">Follow-up Notes</label>
                    <Textarea
                      value={formData.follow_up_notes}
                      onChange={(e) => setFormData({...formData, follow_up_notes: e.target.value})}
                      placeholder="What follow-up actions are needed?"
                      rows={2}
                    />
                  </div>
                )}

                <div className="flex justify-end space-x-3">
                  <Button type="button" variant="outline" onClick={resetForm}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-red-600 hover:bg-red-700">
                    Create Incident Report
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search incidents..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Filter by type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {incidentTypes.map((type) => (
              <SelectItem key={type.value} value={type.value}>
                {type.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={filterSeverity} onValueChange={setFilterSeverity}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Severity" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Severity</SelectItem>
            {severityLevels.map((level) => (
              <SelectItem key={level.value} value={level.value}>
                {level.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-32">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            {statusOptions.map((status) => (
              <SelectItem key={status.value} value={status.value}>
                {status.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={filterDays} onValueChange={setFilterDays}>
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
            <SelectItem value="365">Last year</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="card-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Incidents</p>
                <p className="text-3xl font-bold text-gray-900">{incidents.length}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-orange-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="card-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">High/Critical</p>
                <p className="text-3xl font-bold text-red-600">
                  {incidents.filter(i => i.severity === 'high' || i.severity === 'critical').length}
                </p>
              </div>
              <Shield className="h-8 w-8 text-red-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="card-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Follow-up Needed</p>
                <p className="text-3xl font-bold text-blue-600">
                  {incidents.filter(i => i.follow_up_required).length}
                </p>
              </div>
              <Activity className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="card-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">This Week</p>
                <p className="text-3xl font-bold text-indigo-600">
                  {incidents.filter(i => {
                    const incidentDate = new Date(i.created_at);
                    const weekAgo = new Date();
                    weekAgo.setDate(weekAgo.getDate() - 7);
                    return incidentDate >= weekAgo;
                  }).length}
                </p>
              </div>
              <Calendar className="h-8 w-8 text-indigo-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Incidents List */}
      <div className="space-y-4">
        {filteredIncidents.length === 0 ? (
          <Card className="card-shadow">
            <CardContent className="p-12 text-center">
              <AlertTriangle className="h-16 w-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Incidents Found</h3>
              <p className="text-gray-600">
                {searchTerm || filterType || filterSeverity 
                  ? 'No incidents match your current filters.' 
                  : 'No incident reports have been filed yet.'}
              </p>
            </CardContent>
          </Card>
        ) : (
          filteredIncidents.map((incident) => {
            const typeDetails = getIncidentTypeDetails(incident.incident_type);
            const severityDetails = getSeverityDetails(incident.severity);
            const statusDetails = getStatusDetails(incident.status || 'open');
            
            return (
              <Card key={incident.id} className="card-shadow border-l-4 border-l-red-500">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <AlertTriangle className="h-5 w-5 text-red-600" />
                      <div>
                        <CardTitle className="text-lg">{incident.title}</CardTitle>
                        <CardDescription className="flex items-center space-x-4 mt-1">
                          <span>{formatDateTime(incident.incident_date)}</span>
                          <span>•</span>
                          <span>Reported by {incident.reported_by}</span>
                        </CardDescription>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <Badge className={`px-2 py-1 ${typeDetails.color}`}>
                        {typeDetails.label}
                      </Badge>
                      <Badge className={`px-2 py-1 ${severityDetails.color}`}>
                        {severityDetails.label}
                      </Badge>
                      <Badge className={`px-2 py-1 ${statusDetails.color}`}>
                        {statusDetails.label}
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent className="space-y-4">
                  <p className="text-gray-700">{incident.description}</p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {incident.location && (
                      <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <MapPin className="h-4 w-4" />
                        <span><strong>Location:</strong> {incident.location}</span>
                      </div>
                    )}
                    
                    {incident.resident_names.length > 0 && (
                      <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <Users className="h-4 w-4" />
                        <span><strong>Residents:</strong> {incident.resident_names.join(', ')}</span>
                      </div>
                    )}
                  </div>

                  {/* Incident Flags */}
                  <div className="flex flex-wrap gap-2">
                    {incident.injury_involved && (
                      <Badge variant="outline" className="text-red-700 border-red-300">
                        Injury Involved
                      </Badge>
                    )}
                    {incident.medical_attention_needed && (
                      <Badge variant="outline" className="text-red-700 border-red-300">
                        Medical Attention
                      </Badge>
                    )}
                    {incident.police_contacted && (
                      <Badge variant="outline" className="text-blue-700 border-blue-300">
                        Police Contacted
                      </Badge>
                    )}
                    {incident.follow_up_required && (
                      <Badge variant="outline" className="text-orange-700 border-orange-300">
                        Follow-up Required
                      </Badge>
                    )}
                  </div>

                  {incident.follow_up_notes && (
                    <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                      <p className="text-sm font-medium text-orange-900 mb-1">Follow-up Notes:</p>
                      <p className="text-sm text-orange-800">{incident.follow_up_notes}</p>
                    </div>
                  )}

                  {/* Status History */}
                  {incident.status_history && incident.status_history.length > 1 && (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm font-medium text-blue-900 mb-2 flex items-center">
                        <Activity className="h-4 w-4 mr-1" />
                        Status History ({incident.status_history.length} updates)
                      </p>
                      <div className="space-y-2 max-h-32 overflow-y-auto">
                        {incident.status_history
                          .slice()
                          .reverse() // Show most recent first
                          .map((history, index) => (
                          <div key={index} className="text-xs bg-white p-2 rounded border-l-2 border-blue-300">
                            <div className="flex justify-between items-start mb-1">
                              <Badge 
                                className={`text-xs px-2 py-0.5 ${
                                  history.status === 'open' ? 'bg-red-100 text-red-800' :
                                  history.status === 'in_progress' ? 'bg-yellow-100 text-yellow-800' :
                                  history.status === 'resolved' ? 'bg-green-100 text-green-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}
                              >
                                {history.status.replace('_', ' ').toUpperCase()}
                              </Badge>
                              <span className="text-gray-500">
                                {new Date(history.updated_at).toLocaleDateString('en-US', { 
                                  month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' 
                                })}
                              </span>
                            </div>
                            <p className="text-gray-700 font-medium">{history.updated_by}</p>
                            {history.notes && (
                              <p className="text-gray-600 mt-1 italic">"{history.notes}"</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex justify-between items-center pt-3 border-t border-gray-200">
                    <div className="flex space-x-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openStatusDialog(incident)}
                        className="text-blue-600 hover:text-blue-700"
                      >
                        Update Status
                      </Button>
                      
                      {incident.resident_ids && incident.resident_ids.length > 0 && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            // Find the first resident involved and print their incident reports
                            const residentId = incident.resident_ids[0];
                            const resident = residents.find(r => r.id === residentId);
                            if (resident) {
                              handlePrintIncidentReports(resident);
                            }
                          }}
                          className="text-green-600 hover:text-green-700"
                        >
                          <Printer className="h-4 w-4 mr-1" />
                          Print Report
                        </Button>
                      )}
                    </div>
                    
                    {user?.role === 'admin' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDeleteIncident(incident.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        Delete
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Status Update Dialog */}
      <Dialog open={statusUpdateDialog.open} onOpenChange={(open) => setStatusUpdateDialog({ ...statusUpdateDialog, open })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-blue-700 flex items-center space-x-2">
              <AlertTriangle className="h-5 w-5" />
              <span>Update Incident Status</span>
            </DialogTitle>
            <DialogDescription>
              Update the status of incident: {statusUpdateDialog.incident?.title}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleStatusUpdate} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700">New Status *</label>
              <Select 
                value={statusUpdateData.status} 
                onValueChange={(value) => setStatusUpdateData({...statusUpdateData, status: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {statusOptions.map((status) => (
                    <SelectItem key={status.value} value={status.value}>
                      {status.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Status Update Notes</label>
              <Textarea
                value={statusUpdateData.notes}
                onChange={(e) => setStatusUpdateData({...statusUpdateData, notes: e.target.value})}
                placeholder="Optional notes about this status change..."
                rows={3}
              />
            </div>

            <div className="flex justify-end space-x-3">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setStatusUpdateDialog({ open: false, incident: null })}
              >
                Cancel
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                Update Status
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Print Dialog */}
      {printResident && (
        <ResidentDocumentPrint
          isOpen={showPrintDialog}
          onClose={() => setShowPrintDialog(false)}
          resident={printResident}
          incidentReports={printResident.incidentReports || []}
          printType="incident_reports"
        />
      )}
    </div>
  );
};

export default IncidentReports;